import { useState } from 'react';

const Field = ({ label, required, description, apiMapping, helperText, type }) => (
  <div className="mb-6">
    <label className="block font-mono font-bold text-blue-600">
      {label} <span className="text-black">{type}</span>{' '}
      {required && <span className="text-sm text-red-600">*required*</span>}
    </label>
    <p className="text-sm text-gray-600 wrap-anywhere">
      Config API Mapping: <code style={{ overflowWrap: 'anywhere' }}>{apiMapping}</code>
    </p>
    <p className="text-sm text-gray-600">Description: {description}</p>
    {helperText && <p className="mt-1 text-xs text-blue-500">{helperText}</p>}
  </div>
);

const IFrameConfigForm = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="max-w-xl p-4 mx-auto">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-4 button button-primary has-arrow has-arrow-white"
      >
        {isExpanded ? 'Hide iFrame Config' : 'Show iFrame Config'}
      </button>

      {isExpanded && (
        <div className="p-6 border border-gray-200 rounded-lg shadow">
          <Field
            label="tokenExID"
            required
            apiMapping="data.iframeConfiguration.iqProV2.tokenizationId"
            description="Your TokenID"
            type="string"
          />
          <Field
            label="tokenScheme"
            type="string"
            required
            apiMapping="data.iframeConfiguration.iqProV2.tokenScheme"
            description="Either the name (case insensitive) or the JSON value of the Token Scheme to be used"
          />
          <Field
            label="authenticationKey"
            type="string"
            required
            apiMapping="data.iframeConfiguration.iqProV2.authenticationKey"
            description="The generated Authentication Key"
          />
          <Field
            label="timestamp"
            type="string"
            required
            apiMapping="data.iframeConfiguration.iqProV2.timestamp"
            description="The timestamp when the authentication key was generated"
            helperText="This value must match the one provided when generating your Authentication Key. Format: yyyyMMddHHmmss"
          />
          <Field
            label="origin"
            required
            type="string"
            apiMapping="data.iframeConfiguration.iqProV2.origin"
            description="The fully qualified origin of your application. In the TokenEx Production environment, the origin must use HTTPS."
          />
          <Field
            label="styles"
            type="object"
            apiMapping="N/A"
            description="CSS to be applied to the PAN and CVV inputs (see the Styling the iFrame tab)"
          />
          <Field
            label="placeholders"
            type="string"
            apiMapping="N/A"
            description="The timestamp when the authentication key was generated"
          />
          <Field
            label="pci"
            type="boolean"
            apiMapping="N/A"
            description="Specifies if the PCI version of the iFrame should be rendered; defaults to false"
          />
          <Field
            label="enablePrettyFormat"
            type="string"
            apiMapping="N/A"
            description="If pci is true, then enabling this property will auto format the credit card number as it appears on the physical card"
            helperText="NOTE: This will only apply if input type is set to text. It will not work if input type is set to 'num' or 'tel'."
          />
          <Field
            label="enableValidateOnBlur"
            type="boolean"
            apiMapping="N/A"
            description="If true, then the Validate function will be called when the input loses focus; otherwise, Validate must be invoked manually"
          />
          <Field
            label="inputType"
            type="string"
            apiMapping="N/A"
            description='Allowed values are "number", "tel", and "text". If nothing is supplied, this will default to "text"'
          />
          <Field
            label="allowUnknownCardTypes"
            type="boolean"
            apiMapping="N/A"
            description="If true, then unknown, or types other than 'masterCard', 'americanExpress', 'discover', 'visa', 'diners', or 'jcb' will tokenize; defaults to false."
            helperText="NOTE: If this field is true while using Custom Data Types in PCI w/ CVV or CVV Only modes, the CVV text field's allowable input will be 4. Use the cvvValidRegex field to validate the length of the CVV input."
          />
          <Field
            label="debug"
            type="boolean"
            apiMapping="N/A"
            description="If enabled, data will be output to the console to assist with debugging"
          />
          <Field
            label="customRegEx"
            type="string"
            apiMapping="N/A"
            description="If present, the RegEx expression provided will be used during validation events in addition to any internal validation. Please note that the backslash is an escape character - if used in your RegEx, it will need to be escaped."
          />
          <Field
            label="cvv"
            type="boolean"
            apiMapping="N/A"
            description="Specifies if a CVV iFrame should be rendered; defaults to false."
          />
          <Field
            label="cvvInputType"
            type="string"
            apiMapping="N/A"
            description='Allowed values are "number", "tel", and "text"; if nothing is supplied, this will default to "text".'
          />
          <Field
            label="cvvContainerId"
            type="string"
            apiMapping="CvvTextBox"
            description="The container in which you want to render the CVV iFrame when cvvOnly is false."
          />
          <Field
            label="cvvPlaceholder"
            type="string"
            apiMapping="CVV"
            description="Optionally sets the placeholder attribute of the CVV input when cvvOnly is false."
          />
          <Field
            label="customDataTypes"
            type="object"
            apiMapping="N/A"
            description="Custom validations appended to our standard brand detection (see the Using Custom Data Types tab)"
          />
          <Field
            label="customDataLabel"
            type="string"
            apiMapping="N/A"
            description="Allows customization of the iframe data label. If none supplied, this defaults to 'Data'."
          />
          <Field
            label="customCvvDataLabel"
            type="string"
            apiMapping="N/A"
            description="The timestamp when the authentication key was generated"
          />
          <Field
            label="maskInput"
            type="boolean"
            apiMapping="N/A"
            description="Hides the input being typed by masking it with masking character."
          />
          <Field
            label="font"
            type="string"
            apiMapping="N/A"
            description={
              <>
                A{' '}
                <a className="text-blue-500 underline" href="https://fonts.google.com/">
                  Google Font
                </a>{' '}
                to use in the iframe
              </>
            }
          />
          <Field
            label="title"
            type="string"
            apiMapping="N/A"
            description="Content of the title element used by the iframe document"
          />
          <Field
            label="cvvTitle"
            type="string"
            apiMapping="N/A"
            description="Content of the title element used by the cvv iframe document"
          />
          <Field
            label="enableAriaRequired"
            type="boolean"
            apiMapping="N/A"
            description="If true, sets the aria-required and aria-invalid attributes to true. When Validate function is called aria-invalid will update according to the result of isValid."
          />
          <Field
            label="inputMode"
            type="string"
            apiMapping="N/A"
            description={
              <>
                See{' '}
                <a
                  className="text-blue-500 underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode"
                >
                  MDN Input Mode
                </a>{' '}
                for acceptable modes.
              </>
            }
          />
          <Field
            label="expiresInSeconds"
            type="string"
            apiMapping="N/A"
            description="The expiration time when the authentication key is going to expire after initial load. Valid range: 1 to 1200 Seconds"
            helperText={
              <>
                NOTE: Default expiration time is set to 1200 seconds (20 Minutes). The expiration
                time will be reset to default, in case if user provides value which
                <ul className="mt-3 ml-5 list-disc">
                  <li className="text-xs text-blue-500">Is less than or equal to 0</li>
                  <li className="text-xs text-blue-500">Is greater than 1200</li>
                </ul>
              </>
            }
          />
          <Field
            label="inputMaxLength"
            type="int"
            apiMapping="N/A"
            description="Content of the title element used by the iframe document"
            helperText="NOTE: This parameter only works with input type 'text'."
          />
          <Field
            label="useExtendedBIN"
            type="boolean"
            apiMapping="N/A"
            description="If true, returns the first eight characters of the PAN in the Tokenize response."
            helperText={
              <>
                NOTE: Will not return firstEight in the Tokenize response if:
                <ul className="mt-3 ml-5 list-disc">
                  <li className="text-xs text-blue-500">PCI is false</li>
                  <li className="text-xs text-blue-500">or PAN is fewer than 16 characters.</li>
                </ul>
              </>
            }
          />
          <Field
            label="enableValidateOnKeyUp"
            type="boolean"
            apiMapping="N/A"
            description="If true, then the Validate function will be called after each keystroke; otherwise, Validate must be invoked manually"
            helperText="NOTE: This functionality is only available for PCI and PCIwithCVV iFrame modes"
          />
          <Field
            label="enableValidateOnCvvKeyUp"
            type="boolean"
            apiMapping="N/A"
            description="If true, then the Validate function will be called after each keystroke in the CVV iframe; otherwise, Validate must be invoked manually."
            helperText="NOTE: This functionality is only available for PCI and PCIwithCVV iFrame modes"
          />
          <Field
            label="returnAutoCompleteValues"
            type="boolean"
            apiMapping="N/A"
            description="If true, then this will return nameOnCard and cardExp values whenever a user utilizes the browsers autocomplete functionality."
            helperText="NOTE: Autocomplete must be enabled"
          />
          <Field
            label="cardMaxLength"
            type="object"
            apiMapping="N/A"
            description={
              <>
                EXAMPLE: cardMaxLengths: {'{'}"visa" : 16,"mastercard" : 19,"americanexpress" :
                19,"discover" : 16,"diners" : 16,"jcb" : 19{'}'}
                <ul className="mt-3 ml-5 list-disc">
                  <li className="text-sm text-gray-600">This field is optional.</li>
                  <li className="text-sm text-gray-600">
                    Providing an object for this field requires all properties, i.e. card brands, to
                    be specified on the object.
                  </li>
                  <li className="text-sm text-gray-600">
                    Either all card brand properties on the object must have integer values, or they
                    all must be set to null.
                  </li>
                  <li className="text-sm text-gray-600">
                    If no object is provided for this field, the max length for any card brand will
                    be set to the default max length, which is 19.
                  </li>
                  <li className="text-sm text-gray-600">
                    Required card brands:
                    <ul className="mt-3 ml-5 list-disc">
                      <li className="text-sm text-gray-600">American Express</li>
                      <li className="text-sm text-gray-600">Diners</li>
                      <li className="text-sm text-gray-600">Discover</li>
                      <li className="text-sm text-gray-600">JCB</li>
                      <li className="text-sm text-gray-600">Mastercard</li>
                      <li className="text-sm text-gray-600">Visa</li>
                    </ul>
                  </li>
                </ul>
              </>
            }
            helperText="NOTE: This functionality is only available for PCI and PCIwithCVV iFrame modes. Max Length for PCI and PCIwithCVV is 19 by default."
          />
        </div>
      )}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`my-4 button button-primary has-arrow has-arrow-white ${
          isExpanded ? '' : '!hidden'
        }`}
      >
        {isExpanded ? 'Hide iFrame Config' : 'Show iFrame Config'}
      </button>
    </div>
  );
};

export default IFrameConfigForm;
